import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Card, EmptyState, ErrorNotice, PageHeader, Skeleton, Table, TabPanel, Tabs } from '@/components/ui';
import { useI18n, formatRelative } from '@/i18n';
import { api } from '@/lib/api';
import type { BR } from '@/lib/types';
import { BROKER_ACTIVE, BrokerBrChip, useNextStep } from './common';

type Tab = 'active' | 'awaitingPayment' | 'done' | 'cancelled';

export function BrokerRequests() {
  const { t, locale, localTitle } = useI18n();
  const nextStep = useNextStep();
  const [tab, setTab] = useState<Tab>('active');
  const status = tab === 'active' ? BROKER_ACTIVE.join(',') : tab === 'cancelled' ? 'cancelled' : 'completed';
  const list = useQuery({ queryKey: ['broker', 'requests', tab], queryFn: () => api.get<{ items: BR[] }>(`/api/broker/requests?status=${status}&limit=100`) });
  const rows = (list.data?.items ?? []).filter((r) => (tab === 'awaitingPayment' ? r.commissionStatus === 'due' : tab === 'done' ? r.commissionStatus !== 'due' : true));
  return (
    <div className="flex flex-col gap-6">
      <PageHeader crumbs={[{ to: '/broker', label: t('nav.dashboard') }, { label: t('nav.myRequests') }]} title={t('nav.myRequests')} />
      <Tabs<Tab> id="broker-requests" value={tab} onChange={setTab} items={[{ key: 'active', label: t('broker.requests.tabs.active') }, { key: 'awaitingPayment', label: t('broker.requests.tabs.awaitingPayment') }, { key: 'done', label: t('broker.requests.tabs.done') }, { key: 'cancelled', label: t('broker.requests.tabs.cancelled') }]} />
      <TabPanel id="broker-requests" value={tab}>
        {list.isLoading ? <Skeleton lines={6} /> : list.error ? <ErrorNotice error={list.error} retry={() => void list.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} /> : rows.length ? (
          <Card className="p-0 overflow-hidden">
            <Table className="border-0 rounded-none">
              <thead><tr><th>{t('app.number')}</th><th>{t('app.property')}</th><th>{t('app.owner')}</th><th>{t('app.status')}</th><th>{t('app.nextStep')}</th><th>{t('app.updated')}</th></tr></thead>
              <tbody>
                {rows.map((r) => {
                  const ns = nextStep(r);
                  return (
                    <tr key={r.id}>
                      <td className="num"><Link to={`/broker/requests/${r.id}`}>{r.number}</Link></td>
                      <td>{localTitle(r)}</td>
                      <td>{r.ownerName ?? '—'}</td>
                      <td><BrokerBrChip br={r} /></td>
                      <td className={ns.muted ? 'text-muted' : ''}>{ns.muted ? ns.text : <Link to={r.status === 'completed' ? '/broker/commissions' : `/broker/requests/${r.id}`}>{ns.text}</Link>}</td>
                      <td className="text-muted text-[13px]">{formatRelative(r.updatedAt, locale)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card>
        ) : <EmptyState title={t('broker.requests.empty')} />}
      </TabPanel>
    </div>
  );
}
